import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { DateTime } from 'luxon';
import { BaseChartDirective } from 'ng2-charts';
import { forkJoin } from 'rxjs';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { PaceReportResults } from 'src/app/api/types/recruiting';
import { ChartOptionsService } from 'src/app/services/chart-options.service';

@Component({
  selector: 'app-renewal-pace-graph',
  imports: [BaseChartDirective],
  templateUrl: './renewal-pace-graph.component.html',
  styleUrl: './renewal-pace-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenewalPaceGraphComponent {


  reportYear = input.required<number>()
  minAttendees = input.required<number>()
  maxAttendees = input.required<number>()
  originalLocationIds = input<string[]>([])
  tripLeaders = input<number[]>([])
  consultants = input<number[]>([])

  currentYearParams = computed(() => {
    const year = this.reportYear()
    const minAttendees = this.minAttendees()
    const maxAttendees = this.maxAttendees()

    return {
      tripStartDate: DateTime.fromObject({ year }).startOf('year').toJSDate(),
      tripEndDate: DateTime.fromObject({ year }).endOf('year').toJSDate(),
      renewalStartDate: DateTime.fromObject({ year }).plus({ years: 1 }).startOf('year').toJSDate(),
      renewalEndDate: DateTime.fromObject({ year }).plus({ years: 1 }).endOf('year').toJSDate(),
      minAttendees,
      maxAttendees,
      originalLocationIds: this.originalLocationIds(),
      tripLeaders: this.tripLeaders(),
      consultants: this.consultants()
    }

  })

  previousYearParams = computed(() => {
    const year = this.reportYear()
    const minAttendees = this.minAttendees()
    const maxAttendees = this.maxAttendees()


    return {
      tripStartDate: DateTime.fromObject({ year }).minus({ years: 1 }).startOf('year').toJSDate(),
      tripEndDate: DateTime.fromObject({ year }).minus({ years: 1 }).endOf('year').toJSDate(),
      renewalStartDate: DateTime.fromObject({ year }).startOf('year').toJSDate(),
      renewalEndDate: DateTime.fromObject({ year }).endOf('year').toJSDate(),
      minAttendees,
      maxAttendees,
      originalLocationIds: this.originalLocationIds(),
      tripLeaders: this.tripLeaders(),
      consultants: this.consultants()
    }
  })


  currentYearData = signal<PaceReportResults[]>([])
  previousYearData = signal<PaceReportResults[]>([])

  chartData = computed<ChartData<"line", number[], string>>(() => {

    const returnData: ChartData<"line", number[], string> = {
      datasets: [
        {
          label: 'Report Year',
          data: [],
          ...this.chartOptionsService.getLineStyle(0)
        },
        {
          label: 'Previous Year',
          data: [],
          ...this.chartOptionsService.getLineStyle(1)
        }
      ],
      labels: []
    }

    const currentYearParams = this.currentYearParams()
    const previousYearParams = this.previousYearParams()
    const currentYearData = this.currentYearData()
    const previousYearData = this.previousYearData()

    //set locale so we get the correct end of the week no matter where the user is located
    const paceStart = DateTime.fromJSDate(currentYearParams.tripStartDate).setLocale('en-US')
    const paceEnd = DateTime.fromJSDate(currentYearParams.tripEndDate).setLocale('en-US')

    const prevPaceStart = DateTime.fromJSDate(previousYearParams.tripStartDate).setLocale('en-US')
    const prevPaceEnd = DateTime.fromJSDate(previousYearParams.tripEndDate).setLocale('en-US')

    let finalWeek = paceEnd.weekNumber

    if (paceEnd.month > 1 && finalWeek === 1) {
      //account for week 1 starting at the end of december
      finalWeek = 52
    }

    for (let i = 1; i <= finalWeek; i++) {

      const currentWeekEnd = paceStart.plus({ weeks: i - 1 }).endOf('week', {useLocaleWeeks: true,})
      const previousWeekEnd = prevPaceStart.plus({ weeks: i - 1 }).endOf('week', {useLocaleWeeks: true})

      const currentWeekTrips = currentYearData.filter(data => {
        const tripDate = DateTime.fromJSDate(data.firstTripDate)
        return tripDate.valueOf() < currentWeekEnd.valueOf()
      })

      const currentWeekTotal = currentWeekTrips.map(c => c.tripCount).reduce((prev, curr) => prev + curr, 0)

      const currentWeekRenewals = currentWeekTrips
        .filter(trip => trip.firstRenewalDate && DateTime.fromJSDate(trip.firstRenewalDate).valueOf() < currentWeekEnd.valueOf())
        .map(c => c.renewalCount).reduce((prev, curr) => prev + curr, 0)

      
      const previousWeekTrips = previousYearData.filter(data => {
        const tripDate = DateTime.fromJSDate(data.firstTripDate)
        return tripDate.valueOf() < previousWeekEnd.valueOf()
      })

      const previousWeekTotal = previousWeekTrips.map(c => c.tripCount).reduce((prev, curr) => prev + curr, 0)
      const previousWeekRenewals = previousWeekTrips
        .filter(trip => trip.firstRenewalDate && DateTime.fromJSDate(trip.firstRenewalDate).valueOf() < previousWeekEnd.valueOf())
        .map(c => c.renewalCount).reduce((prev, curr) => prev + curr, 0)


      returnData.datasets[0].data.push(currentWeekTotal > 0 ? Math.round(currentWeekRenewals / currentWeekTotal * 10000) / 100 : 0)
      returnData.datasets[1].data.push(previousWeekTotal > 0 ? Math.round(previousWeekRenewals / previousWeekTotal * 10000) / 100 : 0)

      returnData.labels?.push(currentWeekEnd.toFormat('MMM d'))


    }

    return returnData

  })

  chartOptions: ChartOptions<"line"> = {
    ...this.chartOptionsService.getBasicLineChart({showAllDatasetsOnHover: true}),

   
    scales: {
      y: {
        ticks: {
          
          stepSize: 10,
         
        }
      }
    },
    
    
    

  }



  constructor(
    private recruitingService: RecruitingService,
    private chartOptionsService: ChartOptionsService
  ) {

    effect(() => {
      const year = this.reportYear()

      if (year) {
        this.loadData()
      }

    })

  }



  loadData() {

    forkJoin([
      this.recruitingService.getPaceReportData(this.currentYearParams()),
      this.recruitingService.getPaceReportData(this.previousYearParams())
    ]).subscribe(([currentYearData, previousYearData]) => {

      this.currentYearData.set(currentYearData)
      this.previousYearData.set(previousYearData)

    })

  }

}
